if (!customElements.get('collection-promo-card')) {
  customElements.define('collection-promo-card', class extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.mobileViewport = matchMedia('(max-width: 699px)');
      this.mobileViewport.addEventListener('change', () => this.scheduleFit(), { signal });
      this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
      this.resizeObserver.observe(this);
      this.contentObserver = new MutationObserver(records => {
        if (records.some(record => record.target !== this)) this.scheduleFit();
      });
      this.contentObserver.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['style'] });
      document.fonts.ready.then(() => {
        if (this.isConnected) this.scheduleFit();
      });
      document.fonts.addEventListener('loadingdone', () => this.scheduleFit(), { signal });
      this.scheduleFit();

      this.videos = [...this.querySelectorAll('video')];
      if (!this.videos.length) return;

      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.nearViewport = false;

      this.mobileViewport.addEventListener('change', () => this.syncPlayback(), { signal });
      this.reducedMotion.addEventListener('change', () => this.syncPlayback(), { signal });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });

      this.observer = new IntersectionObserver(entries => {
        this.nearViewport = entries[0].isIntersecting;
        this.syncPlayback();
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
    }

    scheduleFit() {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(() => this.fitContent());
    }

    fitContent() {
      const content = [...this.querySelectorAll('.collection-promo-card__content')]
        .find(element => element.getClientRects().length > 0);
      if (!content || !this.clientWidth) return;

      this.style.setProperty('--promo-fit', '1');
      this.style.removeProperty('--promo-min-height');
      this.classList.remove('collection-promo-card--fit-fallback');
      const fits = () => {
        const box = content.getBoundingClientRect();
        const padding = parseFloat(getComputedStyle(content).paddingBottom);
        return [...content.querySelectorAll('.collection-promo-card__zone, .collection-promo-card__button')].every(element => {
          const rect = element.getBoundingClientRect();
          return rect.bottom <= box.bottom - padding + 1
            && element.scrollWidth <= element.clientWidth + 1
            && [...element.querySelectorAll('p, li')].every(text => text.scrollWidth <= text.clientWidth + 1);
        });
      };
      if (fits()) return;

      let low = 0.35;
      let high = 1;
      this.style.setProperty('--promo-fit', String(low));
      if (!fits()) {
        this.classList.add('collection-promo-card--fit-fallback');
        const padding = parseFloat(getComputedStyle(content).paddingBottom);
        const height = Math.max(content.scrollHeight + padding, this.clientHeight);
        this.style.setProperty('--promo-min-height', `${Math.ceil(height)}px`);
        return;
      }

      for (let step = 0; step < 9; step++) {
        const scale = (low + high) / 2;
        this.style.setProperty('--promo-fit', String(scale));
        if (fits()) low = scale;
        else high = scale;
      }
      this.style.setProperty('--promo-fit', String(low));
    }

    visibleVideo() {
      return this.videos.find(video => video.getClientRects().length > 0);
    }

    syncPlayback() {
      const active = this.visibleVideo();
      this.videos.forEach(video => {
        if (video === active && this.nearViewport && !this.reducedMotion.matches && !document.hidden) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.contentObserver?.disconnect();
      cancelAnimationFrame(this.fitFrame);
      this.videos?.forEach(video => video.pause());
    }
  });
}
